import { ComponentStoreV2Factory } from '../../../../modules/shared/store/common/component-store-v2.factory';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { KeyValue } from '@angular/common';
import { shortDate } from '../../../../modules/shared/pipes/short-date.pipe';
import { localDate } from '../../../../modules/shared/pipes/local-date.pipe';
import { FolderGlobalStore } from '../../../../store/medicalService/folder.global-store';
import { SessionService } from '../../../../services/session.service';
import { OrganizationGlobalStore } from '../../../../store/organization.global-store';
import { ServiceTypeEnum } from '../../../../../../../types/src/services/enum/service-type.enum';
import { getContractSettings } from '../contracts/contract.utils';
import { SocialServiceContractQuantityEnum } from '@libs/types/src/services/enum/social-service-contract-quantity.enum';
import {
    IndividualPlanTargetStatusEnum,
    individualPlanTargetStatusPriority,
} from '@libs/types/src/services/enum/folder/individual-plan-target-status.enum';
import {
    FolderIndividualPlanTargetObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-target.object';

@Injectable()
export class FolderImportantInfoWidgetStore extends ComponentStoreV2Factory {

    public readonly folderStore = inject(FolderGlobalStore);
    public readonly organizationStore = inject(OrganizationGlobalStore);
    public readonly session = inject(SessionService);

    public currentService = this.organizationStore.getServiceBySignalId(this.folderStore.serviceId);

    public isSocial = computed(() => this.currentService()?.type === ServiceTypeEnum.SOCIAL);

    public multiContractsAllowed = computed(() => {
        const serviceSettings = this.currentService()?.serviceSettings;
        if (serviceSettings) {
            return getContractSettings(serviceSettings).quantity === SocialServiceContractQuantityEnum.MULTIPLE;
        }

        return true;
    });

    public prefix = 'person.dashboard.importantInfo.';

    public latestWork = computed(() => {
        const works = this.data.getWorks();
        return works?.['items']?.length ? works.items[0] : null;
    });

    public dataInfo: Signal<KeyValue<string, string>[]> = computed(() => {
        const person = this.folderStore.folder()?.person;

        let data: KeyValue<string, string>[] = [];
        if (person) {
            data = [
                { key: this.prefixedKey('created'), value: shortDate(person.createdAt, this.session.locale) },
                { key: this.prefixedKey('updated'), value: shortDate(person.updatedAt, this.session.locale) },
            ];

            if (this.folderStore.folder()?.archiveUntil) {
                data.push({
                    key: this.prefixedKey('endOfArchive'),
                    value: shortDate(this.folderStore.folder()!.archiveUntil!, this.session.locale),
                });
            }

            if (this.latestWork()) {
                data.push({
                    key: this.prefixedKey('lastWork'),
                    value: localDate(this.latestWork()!['dateFrom'], this.session, 'd.M.yyyy') as string,
                });
            }
        }

        return data;
    });

    public actFolderStatus = computed(() => {
        const items = this.data.getFolderStatuses()?.['items'];
        if (!items || items.length === 0) return null;

        return items?.[0].status;
    });

    public actIndividualPlan = computed(() => {
        const ip = this.data.getIndividualPlans()?.['items']?.[0];
        if (!ip) return null;

        return {
            ...ip,
            targets: [...(ip?.targets ?? [])].sort((a: FolderIndividualPlanTargetObject, b: FolderIndividualPlanTargetObject) => {
                return individualPlanTargetStatusPriority[b.status as IndividualPlanTargetStatusEnum] - individualPlanTargetStatusPriority[a.status as IndividualPlanTargetStatusEnum];
            }),
        };
    });

    private prefixedKey(key: string): string {
        return `person.dashboard.importantInfo.${key}`;
    }

}
